/**
 * Match Filters - Shared types and utilities for Apply Matching tab filtering
 * 
 * This module provides client-side filtering functionality for matched candidates/jobs
 * in the Apply Matching tab of Job Detail and Resume Detail pages.
 */

// ============================================================================
// Types
// ============================================================================

export type ScoreRange = 'all' | 'below60' | '60-70' | '70-80' | '80-90' | '90-100';

export interface FilterState {
  assignedUserId: string | null;  // null = "All Users"
  status: string | null;           // null = "All Statuses"
  scoreRange: ScoreRange;
  isPanelExpanded: boolean;
}

export interface UniqueUser {
  id: string;
  name: string;
}

export interface MatchRecord {
  id?: string;
  matched_by_id?: string;
  matched_by_name?: string;
  status?: string;  // Processing status (completed, processing, pending)
  // Configurable Candidate Status (source of truth)
  candidate_status_id?: string | null;
  status_code?: string | null;
  status_name?: string | null;
  stage_id?: string | null;
  stage_name?: string | null;
  match_score?: number;  // Decimal 0-1
  [key: string]: any;
}

// ============================================================================
// Constants
// ============================================================================

export const SCORE_RANGES = {
  all: { min: 0, max: 1, label: 'All' },
  below60: { min: 0, max: 0.6, label: 'Below 60%' },
  '60-70': { min: 0.6, max: 0.7, label: '60-70%' },
  '70-80': { min: 0.7, max: 0.8, label: '70-80%' },
  '80-90': { min: 0.8, max: 0.9, label: '80-90%' },
  '90-100': { min: 0.9, max: 1.0, label: '90-100%' }
} as const;

export const DEFAULT_FILTER_STATE: FilterState = {
  assignedUserId: null,
  status: null,
  scoreRange: 'all',
  isPanelExpanded: false
};

// ============================================================================
// Utility Functions
// ============================================================================

/**
 * Extract unique users from match records
 * 
 * @param matches - Array of match records
 * @returns Array of unique users with id and name
 */
export function extractUniqueUsers(matches: MatchRecord[]): UniqueUser[] {
  const userMap = new Map<string, string>();
  
  matches.forEach(match => {
    if (match.matched_by_id && match.matched_by_name) {
      userMap.set(match.matched_by_id, match.matched_by_name);
    }
  });
  
  return Array.from(userMap.entries())
    .map(([id, name]) => ({ id, name }))
    .sort((a, b) => a.name.localeCompare(b.name));
}

/**
 * Extract unique candidate statuses present in the given match records.
 * Value is the candidate_status_id; label is the human-readable status_name.
 *
 * @param matches - Array of match records
 * @returns Array of unique status options with labels
 */
export function extractUniqueStatuses(matches: MatchRecord[]): Array<{ value: string; label: string }> {
  const byId = new Map<string, string>();

  matches.forEach(match => {
    if (match.candidate_status_id) {
      byId.set(match.candidate_status_id, match.status_name || match.candidate_status_id);
    }
  });

  return Array.from(byId.entries())
    .map(([value, label]) => ({ value, label }))
    .sort((a, b) => a.label.localeCompare(b.label));
}

/**
 * Apply filters to match records
 * 
 * @param matches - Array of match records to filter
 * @param filterState - Current filter state
 * @returns Filtered array of match records
 */
export function applyFilters(
  matches: MatchRecord[],
  filterState: FilterState
): MatchRecord[] {
  return matches.filter(match => {
    // Filter by assigned user
    if (filterState.assignedUserId !== null) {
      if (match.matched_by_id !== filterState.assignedUserId) {
        return false;
      }
    }
    
    // Filter by candidate status (matches candidate_status_id)
    if (filterState.status !== null) {
      if (match.candidate_status_id !== filterState.status) {
        return false;
      }
    }
    
    // Filter by score range
    if (filterState.scoreRange !== 'all') {
      const range = SCORE_RANGES[filterState.scoreRange];
      // Get score from match_score (0-1 scale)
      const score = match.match_score ?? 0;
      
      // Handle invalid scores
      const validScore = typeof score === 'number' && !isNaN(score) ? score : 0;
      
      // Check if score falls within range
      // Note: Upper bound is exclusive except for the 90-100% range
      if (filterState.scoreRange === '90-100') {
        // For 90-100%, include both bounds (>= 0.9 AND <= 1.0)
        if (!(validScore >= range.min && validScore <= range.max)) {
          return false;
        }
      } else {
        // For other ranges, min is inclusive, max is exclusive (>= min AND < max)
        if (!(validScore >= range.min && validScore < range.max)) {
          return false;
        }
      }
    }
    
    return true;
  });
}

/**
 * Check if any filters are active
 * 
 * @param filterState - Current filter state
 * @returns True if any filter is active (not default)
 */
export function hasActiveFilters(filterState: FilterState): boolean {
  return (
    filterState.assignedUserId !== null ||
    filterState.status !== null ||
    filterState.scoreRange !== 'all'
  );
}

/**
 * Get the count of active filters
 * 
 * @param filterState - Current filter state
 * @returns Number of active filters
 */
export function getActiveFilterCount(filterState: FilterState): number {
  let count = 0;
  if (filterState.assignedUserId !== null) count++;
  if (filterState.status !== null) count++;
  if (filterState.scoreRange !== 'all') count++;
  return count;
}
